import { useRef, useEffect, useState, useCallback } from 'react';
import { Table } from 'antd';
import { Loader } from '../Loader';
import type { TableProps } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import './DataTable.css';

export interface InfiniteScrollProps {
	/** True while fetching the next page */
	isFetchingNextPage: boolean;
	/** Whether more pages are available */
	hasNextPage: boolean;
	/** Call this to load the next page */
	fetchNextPage: () => void;
}

export interface DataTableProps<T extends object> extends Omit<
	TableProps<T>,
	'columns'
> {
	/**
	 * Table columns definition
	 */
	columns: ColumnsType<T>;
	/**
	 * Show serial number column
	 * @default true
	 */
	showSN?: boolean;
	/**
	 * Serial number column title
	 * @default 'S.N'
	 */
	snTitle?: string;
	/**
	 * Serial number column width in px
	 * @default 60
	 */
	snWidth?: number;
	/**
	 * Callback when a row is clicked
	 */
	onRowClick?: (record: T, index?: number) => void;
	/**
	 * Infinite scroll config. When provided, pagination is disabled and the
	 * table fetches more rows on scroll.
	 */
	infiniteScroll?: InfiniteScrollProps;
	/**
	 * Fixed scroll height for the table body (px or CSS string).
	 * When provided, this exact value is used.
	 * When omitted, the table auto-fits to the remaining viewport height.
	 */
	scrollHeight?: number | string;
}

/**
 * Reusable DataTable Component
 *
 * Wraps Ant Design Table with built-in serial number column
 * and infinite scroll pagination.
 */
export function DataTable<T extends object>({
	columns,
	showSN = true,
	snTitle = 'S.N',
	snWidth = 60,
	onRowClick,
	infiniteScroll,
	scrollHeight,
	className,
	scroll,
	pagination,
	...props
}: DataTableProps<T>) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [autoHeight, setAutoHeight] = useState<number | undefined>(undefined);

	// Bottom margin reserved when the table sits alone on a page. Chosen to
	// leave breathing room below the last row so the shadow/scrollbar don't
	// look glued to the viewport edge. If the caller passes an explicit
	// `scrollHeight`, that wins.
	const BOTTOM_RESERVE = 24;
	// AntD table header height (px). Subtracted from the container's y so we
	// can size the scrollable BODY area — otherwise the body area is too
	// small and content clips inside a fixed-height container.
	const HEADER_ROW_HEIGHT = 55;

	const calculateHeight = useCallback(() => {
		if (scrollHeight !== undefined || !containerRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		// The container includes the table header row; AntD sizes `scroll.y`
		// against the BODY area only, so subtract the header from what's
		// available.
		const available = window.innerHeight - rect.top - HEADER_ROW_HEIGHT - BOTTOM_RESERVE;
		setAutoHeight(Math.max(400, available));
	}, [scrollHeight]);

	useEffect(() => {
		calculateHeight();
		window.addEventListener('resize', calculateHeight);
		return () => window.removeEventListener('resize', calculateHeight);
	}, [calculateHeight]);

	// The container's top can shift AFTER first paint — filter widgets render,
	// async fonts load, sidebar collapses. Watch for any layout change that
	// moves the container and recompute. Without this the initial autoHeight
	// is often measured before the surrounding UI has settled, so the table
	// ends up ~40px tall on pages where the layout finishes late.
	useEffect(() => {
		if (scrollHeight !== undefined || !containerRef.current) return;
		const el = containerRef.current;
		// ResizeObserver on the parent catches sibling reflows that shift `el`
		// vertically; direct observation of `el` itself catches width changes.
		const ro = new ResizeObserver(() => calculateHeight());
		ro.observe(el);
		if (el.parentElement) ro.observe(el.parentElement);
		return () => ro.disconnect();
	}, [calculateHeight, scrollHeight]);

	// Keep a ref to the latest infiniteScroll values so the scroll handler
	// always reads fresh state without needing to reattach the listener.
	const infiniteScrollRef = useRef(infiniteScroll);
	useEffect(() => {
		infiniteScrollRef.current = infiniteScroll;
	});

	// Listen for scroll events using the capture phase on our container div.
	// Scroll events don't bubble, but they ARE caught during capture.
	useEffect(() => {
		const container = containerRef.current;
		if (!infiniteScroll || !container) return;

		const handleScroll = (e: Event) => {
			const inf = infiniteScrollRef.current;
			if (!inf || inf.isFetchingNextPage || !inf.hasNextPage) return;

			const target = e.target as HTMLElement;
			if (!target?.classList?.contains('ant-table-body')) return;

			const { scrollTop, scrollHeight: sh, clientHeight } = target;
			if (scrollTop + clientHeight >= sh - 150) {
				inf.fetchNextPage();
			}
		};

		container.addEventListener('scroll', handleScroll, { capture: true, passive: true });
		return () => container.removeEventListener('scroll', handleScroll, { capture: true });
	}, [!!infiniteScroll]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auto-fetch: if the loaded rows don't fill the scroll area (no scrollbar),
	// keep fetching until the content overflows or there are no more pages.
	useEffect(() => {
		if (!infiniteScroll || !containerRef.current) return;
		if (infiniteScroll.isFetchingNextPage || !infiniteScroll.hasNextPage) return;

		// Wait a frame for the DOM to update after new rows render
		const raf = requestAnimationFrame(() => {
			const tableBody = containerRef.current?.querySelector<HTMLElement>('.ant-table-body');
			if (!tableBody) return;

			// No scrollbar yet — content fits entirely within the container
			if (tableBody.scrollHeight <= tableBody.clientHeight) {
				infiniteScroll.fetchNextPage();
			}
		});

		return () => cancelAnimationFrame(raf);
	}, [infiniteScroll, props.dataSource]);

	const snColumn: ColumnsType<T> = showSN
		? [
				{
					title: snTitle,
					key: '__sn',
					width: snWidth,
					align: 'center' as const,
					render: (_: unknown, __: T, index: number) => index + 1,
				},
			]
		: [];

	const mergedColumns = [...snColumn, ...columns];
	const hasFixedHeight = scrollHeight !== undefined;
	const classNames = ['modern-data-table', hasFixedHeight && 'data-table--fixed-height', className]
		.filter(Boolean)
		.join(' ');

	const resolvedScrollY = scrollHeight ?? autoHeight;
	const mergedScroll = {
		x: 'max-content' as const,
		...scroll,
		...(resolvedScrollY !== undefined ? { y: resolvedScrollY } : {}),
	};

	const containerStyle: React.CSSProperties = {
		...(hasFixedHeight
			? { '--table-scroll-height': typeof scrollHeight === 'number' ? `${scrollHeight}px` : scrollHeight }
			: {}),
		...(resolvedScrollY !== undefined
			? { '--table-body-min-height': typeof resolvedScrollY === 'number' ? `${resolvedScrollY}px` : resolvedScrollY }
			: {}),
	} as React.CSSProperties;

	const resolvedPagination = infiniteScroll ? false : (pagination ?? false);

	return (
		<div ref={containerRef} style={containerStyle} className="min-w-0 w-full">
			<Table<T>
				columns={mergedColumns}
				pagination={resolvedPagination}
				className={classNames}
				scroll={mergedScroll}
				onRow={
					onRowClick
						? (record, index) => ({
								onClick: () => onRowClick(record, index),
								style: { cursor: 'pointer' },
							})
						: props.onRow
				}
				{...props}
			/>

			{infiniteScroll && (
				<>
					{infiniteScroll.isFetchingNextPage && (
						<Loader variant="inline" />
					)}
				</>
			)}
		</div>
	);
}

DataTable.displayName = 'DataTable';
